/**
 * Subscription acknowledgment gate.
 *
 * Intercepts Add to Cart on subscription products and requires the customer to
 * acknowledge the monthly service charge before the item is added.
 *
 * Integration note: the theme's <product-form> binds its own 'submit' listener
 * directly on the form. Listeners on the event target itself fire in
 * registration order regardless of the capture flag, so we cannot rely on
 * beating it there. Instead we listen on `document` in the CAPTURE phase, which
 * always runs before any listener on the target, and stop propagation from
 * there. product-form.js is never modified.
 */
(function () {
  'use strict';

  var cfgEl = document.querySelector('[data-subscription-ack-config]');
  var tplEl = document.querySelector('[data-subscription-ack-template]');
  if (!cfgEl || !tplEl) return;

  var CFG;
  try {
    CFG = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return; // Bad config should never block the cart.
  }

  var PRODUCTS = Array.isArray(CFG.products) ? CFG.products : [];
  if (!PRODUCTS.length) return;

  var PRODUCT_BY_VARIANT_ID = {};
  PRODUCTS.forEach(function (product) {
    (product.variants || []).forEach(function (variant) {
      PRODUCT_BY_VARIANT_ID[String(variant.id)] = product;
    });
  });

  var passThroughForm = null; // set for the one confirmed add we allow through

  /* ---------------------------------------------------------------- utils */

  function money(n) {
    return '$' + n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function radioWord(n) {
    return n === 1 ? '1 radio' : n + ' radios';
  }

  /** Pulls a dollar figure out of an option label such as
   *  "30-Month Billing Agreement: $37 Per Month Per Radio". */
  function rateFromLabel(label) {
    if (!label) return null;
    var m = String(label).match(/\$\s*(\d+(?:\.\d{1,2})?)/);
    return m ? parseFloat(m[1]) : null;
  }

  /** "6 Pack" -> 6, "Single" -> 1 */
  function packToCount(label) {
    if (!label) return null;
    if (/single/i.test(label)) return 1;
    var m = String(label).match(/(\d+)\s*pack/i);
    return m ? parseInt(m[1], 10) : null;
  }

  function isTermOption(label) {
    return /30[-\s]?month|billing agreement/i.test(String(label || ''));
  }

  /* ------------------------------------------------- read the current form */

  /** Maps the selected variant id back to its option values. */
  function productForForm(form) {
    var idInput = form.querySelector('[name="id"]');
    return idInput ? PRODUCT_BY_VARIANT_ID[String(idInput.value)] || null : null;
  }

  function selectedOptions(form, product) {
    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return [];
    var id = Number(idInput.value);
    var hit = (product.variants || []).filter(function (v) { return v.id === id; })[0];
    return hit ? hit.options : [];
  }

  function quantityOf(form) {
    var q = form.querySelector('[name="quantity"]');
    var n = q ? parseInt(q.value, 10) : 1;
    return isNaN(n) || n < 1 ? 1 : n;
  }

  /**
   * Works out what this add-to-cart actually commits the customer to.
   * Returns null only if we cannot identify the purchase option at all.
   */
  function describe(form, product) {
    var opts = selectedOptions(form, product);
    if (!opts.length) return null;

    var qty = quantityOf(form);
    var names = product.optionNames || [];

    // Option values are positional, so match them to their option NAME rather
    // than sniffing the value text. Option names are inconsistent across these
    // products ("Purchase Option", "Purchase Option:", "Purchase option"), so
    // match loosely on the name, not exactly.
    var termLabel = null;
    var packLabel = null;
    var safeguardLabel = null;

    names.forEach(function (name, i) {
      var val = opts[i];
      if (val === undefined || val === null) return;
      if (/purchase option/i.test(name)) termLabel = val;
      else if (/safeguard/i.test(name) || (product.handle === "motorola-tlk-200-wave-ptx-broadband-push-to-talk-radio" && /^wave ptx plan$/i.test(name))) safeguardLabel = val;
      else if (/^pack$/i.test(name)) packLabel = val;
    });

    if (termLabel === null) return null;

    // --- radios in this line item
    var perUnit = packToCount(packLabel);
    if (perUnit === null) {
      perUnit = (CFG.unitOverrides && CFG.unitOverrides[product.handle]) || 1;
    }
    var radios = perUnit * qty;

    // --- monthly rate per radio
    var override = CFG.rateOverrides && CFG.rateOverrides[product.handle];
    var rate = null;
    if (override && Object.prototype.hasOwnProperty.call(override, termLabel)) {
      rate = override[termLabel];
    }
    if (rate === null || rate === undefined) rate = rateFromLabel(termLabel);

    // --- safeguard
    var sgRate = 0;
    var sgOn = false;
    if (safeguardLabel && (/^yes/i.test(safeguardLabel) || (product.handle === "motorola-tlk-200-wave-ptx-broadband-push-to-talk-radio" && /^safeguard/i.test(safeguardLabel)))) {
      sgOn = true;
      sgRate = rateFromLabel(safeguardLabel) || 0;
    }

    var known = rate !== null && rate !== undefined;
    var perRadio = known ? rate + sgRate : null;

    return {
      qty: qty,
      radios: radios,
      termLabel: termLabel,
      packLabel: packLabel,
      safeguardLabel: safeguardLabel,
      sgOn: sgOn,
      sgRate: sgRate,
      isTerm: isTermOption(termLabel),
      rateKnown: known,
      perRadio: perRadio,
      monthly: known ? perRadio * radios : null,
      commitment: known && isTermOption(termLabel)
        ? perRadio * radios * (CFG.termMonths || 30)
        : null
    };
  }

  /* -------------------------------------------------------------- the modal */

  var modal = null;

  function buildModal() {
    if (modal) return modal;
    var frag = tplEl.content.cloneNode(true);
    modal = frag.querySelector('.sub-ack');
    document.body.appendChild(modal);

    modal.querySelectorAll('[data-ack-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    modal.querySelector('[data-ack-scrim]').addEventListener('click', close);

    var box = modal.querySelector('[data-ack-checkbox]');
    var go = modal.querySelector('[data-ack-confirm]');
    box.addEventListener('change', function () { go.disabled = !box.checked; });

    return modal;
  }

  function fill(d, product) {
    var m = buildModal();
    var termNoun = d.isTerm ? (CFG.termMonths || 30) + ' months of billing' : 'ongoing monthly billing';

    m.querySelector('[data-ack-intro]').textContent =
      product.title + ' will not transmit without an active service subscription. '
      + (product.handle === 'motorola-tlk-200-wave-ptx-broadband-push-to-talk-radio' && d.isTerm
        ? 'The $0.01 paid today is a setup-request amount, not the hardware price or service charge. Service is billed '
        : 'The amount you pay today covers the hardware only. Service is billed ')
      + 'separately by ' + CFG.biller + '.';

    var cost = m.querySelector('[data-ack-cost]');
    if (d.rateKnown) {
      cost.hidden = false;
      m.querySelector('[data-ack-amount]').innerHTML =
        money(d.monthly) + ' <em>per month</em>';

      var brk;
      if (d.radios === 1) {
        brk = money(d.perRadio - d.sgRate) + ' service'
          + (d.sgOn ? ' plus ' + money(d.sgRate) + ' SafeGuard' : '');
      } else {
        brk = radioWord(d.radios) + ' at ' + money(d.perRadio) + ' per radio, per month';
        if (d.qty > 1 && d.packLabel) {
          brk = d.packLabel + ' × ' + d.qty + ' = ' + brk;
        } else if (d.qty > 1) {
          brk = 'Quantity ' + d.qty + ' = ' + brk;
        }
      }
      if (d.commitment) {
        brk += '. Total over ' + (CFG.termMonths || 30) + ' months: ' + money(d.commitment) + '.';
      }
      m.querySelector('[data-ack-breakdown]').textContent = brk;
    } else {
      // No rate on file for this option. Still gate, just without a figure.
      cost.hidden = true;
    }

    m.querySelector('[data-ack-checklabel]').textContent = d.rateKnown
      ? 'I understand this order requires a monthly subscription of '
        + money(d.monthly) + ', billed separately by ' + CFG.biller
        + ', and I am committing to ' + termNoun + '.'
      : 'I understand this order requires a monthly service subscription billed '
        + 'separately by ' + CFG.biller + ', and I am committing to ' + termNoun + '.';

    var fine = 'Billing is handled directly by ' + CFG.biller
      + ' once your account is registered. ';
    fine += d.rateKnown
      ? 'Questions about your subscription? '
      : 'Our ' + CFG.phoneLabel + ' will confirm your exact monthly rate before activation. ';
    m.querySelector('[data-ack-fine]').innerHTML = fine
      + 'Call our ' + CFG.phoneLabel + ' direct at <a class="sub-ack__tel" href="'
      + CFG.phoneHref + '">' + CFG.phone + '</a>.';

    return m;
  }

  var lastFocus = null;
  var pendingForm = null;
  var pendingDetail = null;
  var pendingProduct = null;
  var pendingTrigger = null;

  function open(form, d, product, trigger) {
    pendingForm = form;
    pendingDetail = d;
    pendingProduct = product;
    pendingTrigger = trigger || null;
    var m = fill(d, product);
    var box = m.querySelector('[data-ack-checkbox]');
    box.checked = false;
    m.querySelector('[data-ack-confirm]').disabled = true;

    lastFocus = document.activeElement;
    m.hidden = false;
    // Force a frame so the transition runs.
    requestAnimationFrame(function () { m.setAttribute('data-open', 'true'); });
    document.documentElement.style.overflow = 'hidden';
    setTimeout(function () { box.focus(); }, 60);

    m.querySelector('[data-ack-confirm]').onclick = confirmAdd;
  }

  function close() {
    if (!modal) return;
    modal.removeAttribute('data-open');
    document.documentElement.style.overflow = '';
    setTimeout(function () { if (modal) modal.hidden = true; }, 200);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    pendingForm = null;
    pendingDetail = null;
    pendingProduct = null;
    pendingTrigger = null;
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal && modal.getAttribute('data-open') === 'true') close();
  });

  /* ----------------------------------------------- write the order record */

  function setProp(form, name, value) {
    var sel = 'input[name="properties[' + name + ']"]';
    var input = form.querySelector(sel);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + name + ']';
      form.appendChild(input);
    }
    input.value = value;
  }

  function stampProperties(form, d, product) {
    [
      '_ack_monthly', '_ack_commitment', '_twrg_monthly_service_value',
      '_twrg_contract_value', '_twrg_contract_months', '_twrg_value_model'
    ].forEach(function (name) {
      var oldProperty = form.querySelector('input[name="properties[' + name + ']"]');
      if (oldProperty) oldProperty.remove();
    });

    // Visible on the cart line and the confirmation, for reinforcement.
    setProp(form, 'Monthly subscription', d.rateKnown
      ? money(d.monthly) + ' per month, billed by ' + CFG.biller
      : 'Required, billed by ' + CFG.biller + '. Rate confirmed by our team.');

    // Hidden (underscore prefix) audit trail on the order.
    setProp(form, '_ack', 'true');
    setProp(form, '_ack_at', new Date().toISOString());
    setProp(form, '_ack_terms', d.termLabel);
    setProp(form, '_ack_safeguard', d.sgOn ? d.safeguardLabel : 'Declined');
    setProp(form, '_ack_radios', String(d.radios));
    setProp(form, '_ack_biller', CFG.biller);
    if (d.rateKnown) {
      setProp(form, '_ack_monthly', money(d.monthly));
      setProp(form, '_twrg_monthly_service_value', d.monthly.toFixed(2));
      if (d.commitment) {
        setProp(form, '_ack_commitment', money(d.commitment));
        setProp(form, '_twrg_contract_value', d.commitment.toFixed(2));
        setProp(form, '_twrg_contract_months', String(CFG.termMonths || 30));
        setProp(form, '_twrg_value_model', 'service_contract_commitment');
      }
    }
  }

  function confirmAdd() {
    var form = pendingForm;
    var d = pendingDetail;
    var product = pendingProduct;
    var trigger = pendingTrigger;
    if (!form || !d || !product) return close();

    var currentProduct = productForForm(form);
    var currentDetail = currentProduct ? describe(form, currentProduct) : null;
    if (!currentDetail) return close();
    if (currentProduct !== product || JSON.stringify(currentDetail) !== JSON.stringify(d)) {
      open(form, currentDetail, currentProduct, trigger);
      return;
    }

    stampProperties(form, d, product);
    if (CFG.oncePerSession) {
      try { sessionStorage.setItem('twrg:sub-ack:' + product.handle, '1'); } catch (e) { /* private mode */ }
    }

    close();

    // Let the original add-to-cart handler run this time.
    passThroughForm = form;
    if (trigger) {
      trigger.click();
      setTimeout(function () { passThroughForm = null; }, 0);
      return;
    }
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
    setTimeout(function () { passThroughForm = null; }, 0);
  }

  /* ------------------------------------------------------------- the gate */

  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!form || form.tagName !== 'FORM') return;
    var product = productForForm(form);
    if (!product) return;

    if (passThroughForm === form) {
      passThroughForm = null;
      return; // our own resubmit, let it through
    }

    if (CFG.oncePerSession) {
      var seen = null;
      try { seen = sessionStorage.getItem('twrg:sub-ack:' + product.handle); } catch (e) { /* ignore */ }
      if (seen) return;
    }

    var d = describe(form, product);
    if (!d) return; // could not identify a subscription option, never block

    evt.preventDefault();
    evt.stopPropagation();
    evt.stopImmediatePropagation();
    open(form, d, product);
  }, true); // capture: runs before the form's own listeners

  // PageFly's product-list button posts its enclosing form with its own
  // JavaScript, bypassing the normal form-submit event used elsewhere in the
  // theme. Gate that click explicitly, then resume the original PageFly click
  // after the customer confirms.
  document.addEventListener('click', function (evt) {
    var trigger = evt.target && evt.target.closest
      ? evt.target.closest('[data-pf-type="ProductATC"]')
      : null;
    if (!trigger) return;

    var form = trigger.closest('form');
    var product = form ? productForForm(form) : null;
    if (!product) return;

    if (passThroughForm === form) return;

    var d = describe(form, product);
    if (!d) return;

    evt.preventDefault();
    evt.stopPropagation();
    evt.stopImmediatePropagation();
    open(form, d, product, trigger);
  }, true);
})();
